import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, ArrowRight } from "lucide-react";

const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-");

export function WizardProgress({ titles, current }) {
  return (
    <div className="mb-8" data-testid="wizard-progress">
      <div className="flex items-center justify-between text-[11px] font-bold tracking-[0.18em] uppercase">
        <span className="text-slate-500">Step {current + 1} of {titles.length}</span>
        <span className="text-[#29324A]">{titles[current]}</span>
      </div>
      <div className="mt-2 h-1.5 bg-slate-100">
        <div className="h-full bg-gradient-to-r from-[#29324A] to-[#C43A89] transition-all duration-300" style={{ width: `${((current + 1) / titles.length) * 100}%` }} />
      </div>
    </div>
  );
}

export function Field({ f, value, onChange }) {
  const set = (v) => onChange(f.key, v);
  const label = (
    <Label className="text-[12px] font-semibold text-[#29324A] mb-1.5 block">
      {f.label}{f.required ? " *" : ""}
    </Label>
  );
  if (f.type === "multi") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (o) => set(arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o]);
    return (
      <div className="sm:col-span-2">
        {label}
        <div className="flex flex-wrap gap-2">
          {f.options.map((o) => (
            <button key={o} type="button" data-testid={`f-${f.key}-${slug(o)}`} onClick={() => toggle(o)}
              className={`text-[12px] px-3 py-2 border transition-colors ${arr.includes(o) ? "bg-[#29324A] border-[#29324A] text-white" : "bg-white border-slate-200 text-slate-700 hover:border-[#29324A]"}`}>
              {o}
            </button>
          ))}
        </div>
      </div>
    );
  }
  if (f.type === "textarea") {
    return (
      <div className="sm:col-span-2">
        {label}
        <Textarea rows={4} data-testid={`f-${f.key}`} value={value || ""} onChange={(e) => set(e.target.value)} placeholder={f.placeholder} />
      </div>
    );
  }
  if (f.type === "select") {
    return (
      <div className={f.full ? "sm:col-span-2" : ""}>
        {label}
        <Select value={value || ""} onValueChange={set}>
          <SelectTrigger data-testid={`f-${f.key}`}><SelectValue placeholder={f.placeholder || "Select"} /></SelectTrigger>
          <SelectContent>
            {f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    );
  }
  return (
    <div className={f.full ? "sm:col-span-2" : ""}>
      {label}
      <Input type={f.type || "text"} data-testid={`f-${f.key}`} value={value || ""} onChange={(e) => set(e.target.value)} placeholder={f.placeholder} />
    </div>
  );
}

export function FieldGrid({ fields, values, onChange }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {fields.map((f) => <Field key={f.key} f={f} value={values[f.key]} onChange={onChange} />)}
    </div>
  );
}

export function validateStep(fields, values) {
  for (const f of fields) {
    if (f.required) {
      const v = values[f.key];
      if (v == null || v === "" || (Array.isArray(v) && v.length === 0)) return f.label;
    }
  }
  return null;
}

export function WizardNav({ onBack, onNext, nextLabel = "Continue", loading = false, showBack = true }) {
  return (
    <div className="mt-8 flex items-center justify-between gap-3">
      {showBack ? (
        <button type="button" data-testid="wizard-back" onClick={onBack}
          className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.18em] uppercase text-slate-500 hover:text-[#29324A]">
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>
      ) : <span />}
      <button type="button" data-testid="wizard-next" disabled={loading} onClick={onNext}
        className="inline-flex items-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] disabled:opacity-60 text-white text-xs font-bold tracking-[0.2em] uppercase px-7 py-4">
        {loading ? "Sending…" : nextLabel} {!loading && <ArrowRight className="h-4 w-4" />}
      </button>
    </div>
  );
}

export function PortalHero({ icon: Icon, eyebrow, title, subtitle }) {
  return (
    <section className="relative bg-gradient-to-br from-[#29324A] via-[#1B1430] to-[#29324A] text-white overflow-hidden">
      <span aria-hidden className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#29324A] via-[#C43A89] to-[#C43A89]" />
      <span aria-hidden className="absolute -top-40 -right-40 w-[28rem] h-[28rem] rounded-full bg-[#C43A89]/25 blur-3xl" />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 relative">
        <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">
          {Icon && <Icon className="h-3 w-3" />} {eyebrow}
        </span>
        <h1 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-tight mt-4 max-w-3xl">{title}</h1>
        {subtitle && <p className="text-[15px] text-slate-200 leading-relaxed mt-5 max-w-2xl">{subtitle}</p>}
      </div>
    </section>
  );
}

export function ComplianceNote() {
  return (
    <p data-testid="compliance-note" className="mt-8 text-[11.5px] text-slate-500 leading-relaxed bg-slate-50 border border-slate-200 p-4">
      The information provided through this tool is general and preliminary in nature. It does not constitute immigration assistance, legal advice, a guarantee of admission, visa approval, permanent residency or any particular outcome. Personalised migration advice will only be provided by a Registered Migration Agent or Australian legal practitioner after reviewing the applicant&apos;s full circumstances and supporting documents.
    </p>
  );
}
